/**
 * Invoice Verification API Hooks - Self-service payment confirmation
 *
 * Provides type-safe API operations for invoice payment verification flow:
 * - Parents submit payment confirmations
 * - Staff approve or reject confirmations
 */

import { toaster } from "@/components/infrastructure"
import { api } from "@/services/api"
import { unwrapStandardResponse } from "@/utils/apiResponse"
import { getErrorMessage } from "@/utils/errorHandler"

import { useApiMutation } from "./useApiMutation"

// Types for payment confirmation
export interface SubmitConfirmationData {
  payment_method: "zelle" | "cash" | "check"
  confirmation_number?: string
}

export interface RejectConfirmationData {
  rejection_reason:
    | "transaction_not_found"
    | "amount_mismatch"
    | "duplicate_submission"
    | "other"
  rejection_notes?: string
}

// Response type (matches InvoiceResponse from backend)
interface InvoiceVerificationResponse {
  id: string
  verification_status?: string
  payment_status?: string
  [key: string]: unknown
}

/**
 * Submit payment confirmation (for parents)
 *
 * Parents use this to submit confirmation of a payment they have made.
 * Staff will then verify the payment and approve or reject it.
 */
export function useSubmitPaymentConfirmation() {
  return useApiMutation<
    InvoiceVerificationResponse,
    unknown,
    { invoiceId: string; data: SubmitConfirmationData }
  >({
    mutationFn: async ({ invoiceId, data }) => {
      const response = await api.instance.post(
        `/api/v1/invoices/${invoiceId}/submit-confirmation`,
        data,
      )
      return unwrapStandardResponse<InvoiceVerificationResponse>(
        response.data,
      ) as InvoiceVerificationResponse
    },
    invalidateQueries: [["invoices"], ["enrollments"]],
    successToast: {
      title: "Payment confirmation submitted",
      description: "Staff will verify within 1-2 business days.",
    },
    onError: (error) => {
      toaster.create({
        title: "Failed to submit confirmation",
        description: getErrorMessage(error) || "Please try again",
        type: "error",
      })
    },
  })
}

/**
 * Approve payment confirmation (for staff)
 *
 * Staff use this to approve a pending payment confirmation.
 * This marks the invoice as paid and confirms the enrollment.
 */
export function useApprovePaymentConfirmation() {
  return useApiMutation<InvoiceVerificationResponse, unknown, string>({
    mutationFn: async (invoiceId) => {
      const response = await api.instance.post(
        `/api/v1/invoices/${invoiceId}/approve`,
      )
      return unwrapStandardResponse<InvoiceVerificationResponse>(
        response.data,
      ) as InvoiceVerificationResponse
    },
    invalidateQueries: [["invoices"], ["enrollments"]],
    successToast: {
      title: "Payment approved",
      description: "Enrollment has been confirmed.",
    },
    onError: (error) => {
      toaster.create({
        title: "Failed to approve payment",
        description: getErrorMessage(error) || "Please try again",
        type: "error",
      })
    },
  })
}

/**
 * Reject payment confirmation (for staff)
 *
 * Staff use this to reject a payment confirmation that cannot be verified.
 * The parent will be notified and can re-submit with correct information.
 */
export function useRejectPaymentConfirmation() {
  return useApiMutation<
    InvoiceVerificationResponse,
    unknown,
    { invoiceId: string; data: RejectConfirmationData }
  >({
    mutationFn: async ({ invoiceId, data }) => {
      const response = await api.instance.post(
        `/api/v1/invoices/${invoiceId}/reject`,
        data,
      )
      return unwrapStandardResponse<InvoiceVerificationResponse>(
        response.data,
      ) as InvoiceVerificationResponse
    },
    invalidateQueries: [["invoices"], ["enrollments"]],
    successToast: {
      title: "Payment rejected",
      description: "Parent will be notified and can re-submit.",
    },
    onError: (error) => {
      toaster.create({
        title: "Failed to reject payment",
        description: getErrorMessage(error) || "Please try again",
        type: "error",
      })
    },
  })
}
